import type { Recommendation } from '@/lib/orientationApi'

interface Props {
  recommendation: Recommendation
  onChoose: (structureId: string) => void
  isConfirming: boolean
}

export default function RecommendationCard({ recommendation: rec, onChoose, isConfirming }: Props) {
  const scoreColor = rec.totalScore >= 70 ? 'bg-success-500' : rec.totalScore >= 40 ? 'bg-warning-500' : 'bg-error-500'
  const rankBadge = rec.rank === 1 ? 'bg-yellow-400 text-yellow-900' : rec.rank === 2 ? 'bg-neutral-300 text-neutral-800' : 'bg-orange-300 text-orange-900'

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${rankBadge}`}>
            #{rec.rank}
          </span>
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">{rec.name}</h3>
            <p className="text-xs text-neutral-500">{rec.code}</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold text-neutral-900">{rec.totalScore}</span>
          <span className="text-xs text-neutral-500">/100</span>
        </div>
      </div>

      {/* Score bar */}
      <div className="h-2 bg-neutral-100 rounded-full mb-3">
        <div className={`h-full rounded-full ${scoreColor}`} style={{ width: `${rec.totalScore}%` }} />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-neutral-400">Distance:</span>
          <span className="font-medium text-neutral-700">{rec.distanceKm} km</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-neutral-400">Lits:</span>
          <span className="font-medium text-neutral-700">
            {rec.bedsAvailable !== null ? rec.bedsAvailable : '?'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${rec.hasSpecialist ? 'bg-success-500' : 'bg-neutral-300'}`} />
          <span className="text-neutral-600">Specialiste</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${rec.hasStocks ? 'bg-success-500' : 'bg-neutral-300'}`} />
          <span className="text-neutral-600">Stocks</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-neutral-400">Fraicheur:</span>
          <span className={`font-medium ${
            rec.freshness === 'confirmed' ? 'text-success-600' :
            rec.freshness === 'recent' ? 'text-yellow-600' : 'text-neutral-400'
          }`}>
            {rec.freshness === 'confirmed' ? 'Confirme' :
             rec.freshness === 'recent' ? 'Recent' :
             rec.freshness === 'stale' ? 'Ancien' : 'Inconnu'}
          </span>
        </div>
      </div>

      {/* Choose button */}
      <button
        onClick={() => onChoose(rec.structureId)}
        disabled={isConfirming}
        className="w-full bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
      >
        {isConfirming ? 'Confirmation...' : 'Choisir cette structure'}
      </button>
    </div>
  )
}
